import { useEffect, useState } from 'react';
import { type SupportAccessDto } from '@musleempay/shared';
import { Button } from '@musleempay/ui';
import { adminService } from '../../services/index.js';
import { clearSupportGrant } from '../../lib/support-access.js';

/**
 * Shown across the bottom of the dashboard while a platform operator is looking
 * at a store under a support grant — and only then. It states plainly that the
 * view is read-only, counts down to when access lapses, and is the way out.
 *
 * Fixed to the bottom of the content area (clear of the fixed sidebar on wide
 * screens) so it is always in view and never collides with the sticky header.
 *
 * Leaving does a full page load back to the platform area rather than a router
 * navigation, so nothing from the store stays cached in memory afterwards.
 */

function formatRemaining(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function SupportAccessBanner({ access }: { access: SupportAccessDto }): JSX.Element {
  const expiresAt = new Date(access.expiresAt).getTime();
  const [remaining, setRemaining] = useState(() => expiresAt - Date.now());
  const [isLeaving, setIsLeaving] = useState(false);

  const leave = async (): Promise<void> => {
    setIsLeaving(true);
    try {
      // Best effort: even if ending it fails, the grant lapses on its own and
      // dropping the local copy below stops this tab using it.
      await adminService.endSupportAccess(access.grantId);
    } catch {
      // Nothing useful to tell the operator — they are leaving either way.
    } finally {
      clearSupportGrant();
      window.location.assign('/admin');
    }
  };

  useEffect(() => {
    const timer = setInterval(() => setRemaining(expiresAt - Date.now()), 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);

  // The server stops honouring the grant at the same moment, so the next request
  // would fail anyway. Leave cleanly rather than let the page start erroring.
  useEffect(() => {
    if (remaining <= 0) {
      clearSupportGrant();
      window.location.assign('/admin');
    }
  }, [remaining]);

  return (
    <div
      role="status"
      className="fixed inset-x-0 bottom-0 z-30 flex flex-wrap items-center justify-between gap-3 border-t border-amber-300 bg-amber-100 px-4 py-2.5 text-sm text-amber-950 shadow-[0_-2px_8px_rgba(0,0,0,0.06)] sm:px-6 lg:left-64"
    >
      <p>
        <span className="font-semibold">Viewing {access.merchant.name}</span> — view only, nothing can
        be changed. Access ends in{' '}
        <span className="font-mono font-semibold tabular-nums">{formatRemaining(remaining)}</span>.
      </p>
      <Button
        size="sm"
        variant="secondary"
        isLoading={isLeaving}
        disabled={isLeaving}
        onClick={() => void leave()}
      >
        Exit
      </Button>
    </div>
  );
}
